import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogService, PickWave, PickWaveReservationSummary } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-pick-wave-detail',
  imports: [
    AinPageComponent, AinStateComponent, InventoryPageHintComponent, ButtonModule, TableModule,
    RouterLink, AinCanAccessDirective
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('catalog.pickWaves.detailDescription')">
      <ain-inventory-page-hint pageKey="pickWaveDetail" />
      <a class="back-link" routerLink="/inventory/pick-waves">{{ t('catalog.pickWaves.backToList') }}</a>
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
      } @else if (!wave()) {
        <ain-state type="empty" [title]="t('catalog.pickWaves.notFound')" />
      } @else {
        <section class="card summary">
          <span>{{ t('catalog.pickWaves.referenceNumber') }}: <strong>{{ wave()!.referenceNumber }}</strong></span>
          <span>{{ t('columns.status') }}: <strong>{{ wave()!.status }}</strong></span>
          <span>{{ t('catalog.inventory.occurredAt') }}: <strong>{{ wave()!.createdAtUtc }}</strong></span>
          @if (wave()!.status === 'Draft') {
            <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.pickWaves.release')" (click)="release()" [disabled]="busy()"></button>
          }
        </section>

        <section class="card">
          <h3>{{ t('catalog.pickWaves.reservationsTitle') }}</h3>
          <p-table [value]="waveLines()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.outbound.sourceReference') }}</th>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th>{{ t('catalog.inventory.quantity') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.sourceType }} — {{ row.sourceReference }}</td>
                <td>{{ offeringLabel(row.offeringId) }}</td>
                <td>{{ warehouseLabel(row.warehouseId) }}</td>
                <td>{{ row.quantity }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .back-link { display: inline-block; margin-bottom: 8px; color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #fff; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }
    .error { color: #b91c1c; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryPickWaveDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly wave = signal<PickWave | null>(null);
  readonly waveLines = signal<PickWaveReservationSummary[]>([]);
  private offerings = new Map<string, string>();
  private warehouses = new Map<string, string>();
  t = (k: string) => this.localization.translate(k);
  pageTitle = () => this.wave()?.referenceNumber ?? this.t('catalog.pickWaves.detailTitle');

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload(false)
    );
    this.service.listOfferings().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: o => o.forEach(x => this.offerings.set(x.id, x.code))
    });
    this.service.listWarehouses().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: w => w.forEach(x => this.warehouses.set(x.id, x.code))
    });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(map => {
      const id = map.get('id');
      if (id) this.reload(true, id);
    });
  }

  offeringLabel(id: string): string { return this.offerings.get(id) ?? '-'; }
  warehouseLabel(id: string): string { return this.warehouses.get(id) ?? '-'; }

  release(): void {
    const wave = this.wave();
    if (!wave) return;
    this.busy.set(true);
    this.service.releasePickWave(wave.id).subscribe({
      next: () => this.reload(false, wave.id),
      error: () => {
        this.error.set(this.t('catalog.pickWaves.releaseFailed'));
        this.busy.set(false);
      }
    });
  }

  reload(showLoading = true, waveId?: string): void {
    const id = waveId ?? this.route.snapshot.paramMap.get('id') ?? '';
    if (!id) return;
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.getPickWave(id).subscribe({
      next: found => {
        this.wave.set(found);
        this.waveLines.set(found.reservationSummaries ?? []);
        this.permissionDenied.set(false);
        this.loading.set(false);
        this.busy.set(false);
      },
      error: err => {
        this.wave.set(null);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('catalog.pickWaves.loadFailed'));
        this.loading.set(false);
      }
    });
  }
}
